import React, { useEffect, useState } from "react";
import BaseApi from "../../Http/BaseApi";
import Alert from "./Alert";

/**
 * Self-service optional 2FA toggle (OTP required to enable and disable).
 * Channel: phone (SMS) or email. Admin-compulsory accounts cannot change this.
 */
export default function StaffTfaSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");
  const [pending, setPending] = useState(null); // 'enable' | 'disable' | null
  const [channel, setChannel] = useState("phone");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadStatus = async () => {
    try {
      const res = await BaseApi("staff").get("tfa/status");
      setStatus(res.data);
      if (res.data?.tfa_channel === "email" || res.data?.tfa_channel === "phone") {
        setChannel(res.data.tfa_channel);
      }
    } catch (e) {
      setError(
        e?.response?.data?.message || e.message || "Failed to load 2FA status"
      );
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const requestOtp = async (purpose) => {
    setLoading(true);
    setError("");
    setMessage("");
    setOtp("");
    try {
      const body = purpose === "enable" ? { channel } : {};
      const res = await BaseApi("staff").post(`tfa/${purpose}/request`, body);
      setPending(purpose);
      setMessage(res.data?.message || "OTP sent.");
      if (status) {
        setStatus({
          ...status,
          phone_masked: res.data.phone_masked ?? status.phone_masked,
          email_masked: res.data.email_masked ?? status.email_masked,
        });
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Request failed");
    }
    setLoading(false);
  };

  const confirmOtp = async () => {
    if (!pending || otp.length < 6) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const res = await BaseApi("staff").post(`tfa/${pending}/confirm`, {
        otp,
      });
      setMessage(res.data?.message || "Updated.");
      setPending(null);
      setOtp("");
      setStatus({
        tfa_compulsory: res.data.tfa_compulsory,
        tfa_enabled: res.data.tfa_enabled,
        tfa_active: res.data.tfa_active,
        tfa_channel: res.data.tfa_channel,
        has_phone: res.data.has_phone ?? status?.has_phone,
        has_email: res.data.has_email ?? status?.has_email,
        phone_masked: res.data.phone_masked ?? status?.phone_masked,
        email_masked: res.data.email_masked ?? status?.email_masked,
        can_disable: res.data.can_disable,
        can_choose_channel: res.data.can_choose_channel,
      });
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Verification failed");
    }
    setLoading(false);
  };

  if (!status) {
    return (
      <div className='p-4 text-sm text-gray-600'>Loading 2FA settings...</div>
    );
  }

  const destinationReady =
    channel === "email" ? status.has_email : status.has_phone;
  const activeChannel = status.tfa_channel || channel;
  const channelLabel = activeChannel === "email" ? "email" : "mobile";

  return (
    <div className='p-5 mt-6 border border-gray-200 rounded-md bg-gray-50'>
      <h3 className='text-lg font-semibold text-gray-800'>
        Login security (2FA)
      </h3>
      <p className='mt-1 text-sm text-gray-600'>
        When enabled, login requires a one-time code sent to your registered
        mobile number or email.
      </p>

      {error && (
        <div className='mt-3'>
          <Alert message={error} />
        </div>
      )}
      {message && !error && (
        <p className='mt-3 text-sm text-green-700'>{message}</p>
      )}

      <div className='mt-4 space-y-1 text-sm text-gray-700'>
        <p>
          Status:{" "}
          <strong>
            {status.tfa_active ? "Active" : "Off"}
            {status.tfa_compulsory ? " (required by admin)" : ""}
          </strong>
        </p>
        {Boolean(status.tfa_active) && (
          <p>
            Method:{" "}
            <strong>{activeChannel === "email" ? "Email" : "Mobile"}</strong>
          </p>
        )}
        {status.phone_masked && <p>Mobile: {status.phone_masked}</p>}
        {status.email_masked && <p>Email: {status.email_masked}</p>}
      </div>

      {Boolean(status.can_choose_channel) && !pending && (
        <div className='mt-4'>
          <p className='mb-2 text-sm font-medium text-gray-700'>
            Send OTP to
          </p>
          <div className='flex flex-wrap gap-4 text-sm'>
            <label className='inline-flex items-center gap-2'>
              <input
                type='radio'
                name='tfa_channel'
                value='phone'
                checked={channel === "phone"}
                onChange={() => setChannel("phone")}
              />
              Mobile
              {!status.has_phone && (
                <span className='text-red-600'>(not registered)</span>
              )}
            </label>
            <label className='inline-flex items-center gap-2'>
              <input
                type='radio'
                name='tfa_channel'
                value='email'
                checked={channel === "email"}
                onChange={() => setChannel("email")}
              />
              Email
              {!status.has_email && (
                <span className='text-red-600'>(not registered)</span>
              )}
            </label>
          </div>
        </div>
      )}

      {!pending && (
        <div className='flex flex-wrap gap-3 mt-4'>
          {!status.tfa_enabled && !status.tfa_compulsory && (
            <button
              type='button'
              disabled={loading || !destinationReady}
              onClick={() => requestOtp("enable")}
              className='px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50'
            >
              Enable 2FA
            </button>
          )}
          {Boolean(status.can_disable) && (
            <button
              type='button'
              disabled={loading}
              onClick={() => requestOtp("disable")}
              className='px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50'
            >
              Disable 2FA
            </button>
          )}
          {Boolean(status.tfa_compulsory) && (
            <p className='text-sm text-amber-700'>
              Admin has made 2FA compulsory via {channelLabel}. You cannot turn
              it off or change the method.
            </p>
          )}
        </div>
      )}

      {pending && (
        <div className='mt-4 space-y-3'>
          <p className='text-sm text-gray-700'>
            Enter the OTP sent to your {channelLabel} to {pending} 2FA.
          </p>
          <input
            type='text'
            inputMode='numeric'
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
            placeholder='6-digit OTP'
            className='block w-full max-w-xs px-3 py-2 border border-gray-300 rounded-md sm:text-sm'
          />
          <div className='flex gap-3'>
            <button
              type='button'
              disabled={loading || otp.length < 6}
              onClick={confirmOtp}
              className='px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50'
            >
              Confirm
            </button>
            <button
              type='button'
              disabled={loading}
              onClick={() => {
                setPending(null);
                setOtp("");
                setMessage("");
              }}
              className='px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md'
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
